import React from 'react';
import { Menu, Transition } from '@headlessui/react';
import { ChevronDownIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import FilterDropdown, { FilterSection } from '../../components/FilterDropdown';
import ActiveFilterChips, { FilterChip } from '../../components/ActiveFilterChips';
import { DASHBOARD_VIEWS, type DashboardViewId } from './types';
import KpiAssignmentGroupQuickSelect from './KpiAssignmentGroupQuickSelect';
import type { Group } from './types';

export interface KpiDashboardFilterBarProps {
  activeViewId: DashboardViewId;
  onViewChange: (viewId: DashboardViewId) => void;
  sections: FilterSection[];
  values: Record<string, string | string[]>;
  onFilterChange: (sectionId: string, value: string | string[]) => void;
  chips: FilterChip[];
  groups: Group[];
  assignmentGroupQuickValue: string;
  onAssignmentGroupQuickChange: (groupId: string) => void;
  onRemoveChip: (chipId: string) => void;
  onClearAllFilters: () => void;
  /** When false, export menu is hidden (e.g. MSR report exports live above report content). */
  showExport?: boolean;
  exportPdfDisabled?: boolean;
  exportExcelDisabled?: boolean;
  onExportPdf: () => void;
  onExportExcel: () => void;
  exportShowExcel?: boolean;
}

const KpiDashboardFilterBar: React.FC<KpiDashboardFilterBarProps> = ({
  activeViewId,
  onViewChange,
  sections,
  values,
  onFilterChange,
  chips,
  groups,
  assignmentGroupQuickValue,
  onAssignmentGroupQuickChange,
  onRemoveChip,
  onClearAllFilters,
  showExport = true,
  exportPdfDisabled = false,
  exportExcelDisabled = false,
  onExportPdf,
  onExportExcel,
  exportShowExcel = true,
}) => {
  const activeViewLabel = DASHBOARD_VIEWS.find((v) => v.id === activeViewId)?.label ?? 'Dashboard';

  return (
    <div className="relative z-50 mx-4 mt-4 shrink-0 sm:mx-5">
      <div className="rounded-xl border border-slate-200/90 bg-white shadow-md shadow-slate-900/5 ring-1 ring-slate-950/[0.04]">
        <div className="h-1 rounded-t-xl bg-gradient-to-r from-indigo-500 via-violet-500 to-sky-500" aria-hidden />
        <div className="px-4 py-3.5 sm:px-5">
          <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3 sm:gap-4">
              <Menu as="div" className="relative inline-block text-left">
                <Menu.Button className="inline-flex min-w-[200px] items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-left text-sm font-semibold text-slate-900 shadow-sm transition-all hover:border-slate-300 hover:bg-slate-50/80 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:min-w-[220px]">
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                    <ChartBarIcon className="h-4 w-4 shrink-0" aria-hidden />
                  </span>
                  <span className="truncate">{activeViewLabel}</span>
                  <ChevronDownIcon className="ml-auto h-5 w-5 shrink-0 text-slate-400" aria-hidden />
                </Menu.Button>
                <Transition
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute left-0 z-[60] mt-2 w-64 origin-top-left rounded-xl border border-slate-200/80 bg-white py-1 shadow-xl shadow-slate-900/10 ring-1 ring-slate-950/5 focus:outline-none">
                    <div className="border-b border-slate-100 px-3 py-2.5">
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Dashboard view</p>
                    </div>
                    {DASHBOARD_VIEWS.map((view) => (
                      <Menu.Item key={view.id}>
                        {({ active }) => (
                          <button
                            type="button"
                            onClick={() => onViewChange(view.id)}
                            className={`flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm transition-colors ${
                              activeViewId === view.id
                                ? 'bg-indigo-50 font-semibold text-indigo-800'
                                : active
                                  ? 'bg-slate-50 text-slate-900'
                                  : 'text-slate-700'
                            }`}
                          >
                            {activeViewId === view.id && (
                              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-600" aria-hidden />
                            )}
                            <span className={activeViewId === view.id ? 'font-semibold' : ''}>{view.label}</span>
                          </button>
                        )}
                      </Menu.Item>
                    ))}
                  </Menu.Items>
                </Transition>
              </Menu>
              <FilterDropdown sections={sections} values={values} onChange={onFilterChange} />
            </div>
            <KpiAssignmentGroupQuickSelect
              groups={groups}
              value={assignmentGroupQuickValue}
              onChange={onAssignmentGroupQuickChange}
            />
          </div>
        </div>
        <div className="rounded-b-xl border-t border-slate-100 bg-slate-50/50 px-4 py-3 sm:px-5">
          <ActiveFilterChips chips={chips} onRemove={onRemoveChip} onClearAll={onClearAllFilters} />
        </div>
      </div>
    </div>
  );
};

export default KpiDashboardFilterBar;
